<template>
  <div class="mt-4">
    <load-and-error :loading="loading" :error="error" can-retry="true" @retry="refresh()"></load-and-error>

    <div v-if="!loading && !error && part">
        <div class="row mb-4">
            <div class="col-md-6">
                <h1 class="mb-4">
                    <router-link :to="{name: 'part', params: {id: part.id}}">
                        <i class="fa fa-chevron-left"></i>
                    </router-link>

                    Stock History
                    {{part.name}}
                </h1>
            </div>
            <div class="col-md-6">
                <select class="form-control" v-model="locationFilter">
                    <option value="">All Locations</option>
                    <option v-for="(display, location) in LOCATIONS" :key="location" :value="location">{{display}}</option>
                </select>
            </div>
        </div>

        <div class="row">
            <div class="col-md-6" v-for="stock in history" :key="stock.id">
                <div class="card mb-4">
                    <div class="card-body">
                        <h5 class="card-title" v-if="stock.quantity >= 0">
                            {{stock.created_at|fulldate}}: Added Stock ({{stock.quantity}})
                        </h5>
                        <h5 class="card-title" v-else>
                            {{stock.created_at|fulldate}}: Removed Stock ({{stock.quantity|abs}})
                        </h5>

                        <h6 class="card-subtitle mb-2 text-muted">
                            {{stock.location|locationName}} - {{stock.storage}}
                        </h6>

                        <div class="card-text">
                            <div v-if="stock.price">
                                Price: {{stock.price|money}}
                            </div>
                            <div v-if="stock.supplier">
                                Supplier: {{stock.supplier}}
                            </div>
                            <div v-if="stock.work_order_type">
                                Work Order Type: {{stock.work_order_type|workOrderType(workOrderTypes)}}
                            </div>
                            <div v-if="stock.unit && stock.unit != 'none'">
                                Unit {{stock.unit|titleCase}}
                                <span v-if="stock.unit_number">
                                    ({{stock.unit_number}})
                                </span>
                            </div>
                            <div v-if="stock.charge">
                                Charge: {{stock.charge}}
                            </div>
                            <div v-if="stock.notes">
                                Notes: {{stock.notes}}
                            </div>
                            <div v-if="stock.created_by">
                                Changed By: {{stock.created_by|user(users)}}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <h2 v-if="history.length == 0">No stock history</h2>
        <nav v-if="pageCount > 1">
            <ul class="pagination justify-content-center">
                <li class="page-item text-success" :class="{disabled: page <= 0}">
                    <a class="page-link" @click="previousPage()">
                        <i class="fa fa-chevron-left"></i>
                    </a>
                </li>
                <li class="page-item disabled" aria-current="page">
                    <a class="page-link">{{page + 1}} of {{pageCount}}</a>
                </li>
                <li class="page-item text-success" :class="{disabled: page + 1 >= pageCount}">
                    <a class="page-link" @click="nextPage()">
                        <i class="fa fa-chevron-right"></i>
                    </a>
                </li>
            </ul>
        </nav>

        <div class="row mt-4">
            <div class="col-md-6 mb-4">
                <router-link
                    :to="{name: 'add_stock', params: {id: part.id}}"
                    class="btn btn-outline-success w-100"
                >
                    <i class="fa fa-plus"></i> Add Stock
                </router-link>
            </div>

            <div class="col-md-6 mb-4">
                <router-link
                    :to="{name: 'remove_stock', params: {id: part.id}}"
                    class="btn btn-outline-success w-100"
                >
                    <i class="fa fa-minus"></i> Remove Stock
                </router-link>
            </div>
        </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import api from 'ays-shared/api';
import utils from 'ays-shared/utils';
import constants from '@/constants';

export default {
  name: 'StockHistory',
  data() {
    return {
      loading: false,
      saving: false,
      error: '',
      part: null,
      history: [],
      locationFilter: '',
      page: 0,
      count: 0,
      pageCount: 1,
      LOCATIONS: constants.LOCATIONS,
    };
  },
  mounted() {
    this.refresh();
  },
  computed: mapState({ users: (state) => state.cache.users, workOrderTypes: (state) => state.cache.workOrderTypes }),
  methods: {
    async refresh() {
      this.error = '';
      this.loading = true;

      try {
        const [part, stock] = await Promise.all([
          this.part ? this.part : api.inventory.get(this.$route.params.id),
          api.inventory.stock.get(this.$route.params.id, this.locationFilter, this.page),
        ]);

        this.part = part;
        this.pageCount = Math.ceil(stock.count / api.LIMIT);
        this.count = stock.count;
        this.history = stock.results;
      }
      catch (err) {
        this.part = null;
        this.history = [];
        this.error = utils.errorMessage(err);
        utils.captureException(err, 'StockHistory');
      }
      finally {
        this.loading = false;
      }
    },
    previousPage() {
      if (this.page > 0) {
        this.page--;
        this.refresh();
      }
    },
    nextPage() {
      if (this.page < this.pageCount) {
        this.page++;
        this.refresh();
      }
    },
  },
  filters: {
    abs(value) {
      return Math.abs(value);
    },
    user(id, users) {
      return users.find((user) => user.id == id).name;
    },
    workOrderType(id, workOrderTypes) {
      return workOrderTypes.find((workOrderType) => workOrderType.id == id).name;
    },
  },
  watch: {
    locationFilter() {
      this.refresh();
    },
  },
};
</script>
